import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import type { ScrapingZone } from "@/types/scraping-zones";

export function useScrapingZones() {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["scraping-zones", teamId],
    queryFn: async () => {
      if (!teamId) return [];

      const { data, error } = await supabase
        .from("teams")
        .select("options")
        .eq("id", teamId)
        .single();

      if (error) throw error;

      const settings = (data?.options as Record<string, unknown>) ?? {};
      return (settings.scraping_zones as ScrapingZone[]) ?? [];
    },
    enabled: !!teamId,
  });
}

export function useUpdateScrapingZones() {
  const { teamId } = useTeamId();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (zones: ScrapingZone[]) => {
      if (!teamId || !user) throw new Error("Not authenticated");

      // 1. Lire settings actuels pour ne pas ecraser d'autres cles
      const { data: team, error: readError } = await supabase
        .from("teams")
        .select("options")
        .eq("id", teamId)
        .single();

      if (readError) throw readError;

      const currentSettings = (team?.options as Record<string, unknown>) ?? {};

      // 2. Ecrire scraping_zones dans teams.settings
      const { error: writeError } = await supabase
        .from("teams")
        .update({
          options: {
            ...currentSettings,
            scraping_zones: zones,
          },
        })
        .eq("id", teamId);

      if (writeError) throw writeError;

      // 3. Sync inverse → agent_profiles.zones (noms de ville)
      const cityNames = zones.map((z) => z.city);
      await supabase
        .from("agent_profiles")
        .update({ zones: cityNames })
        .eq("user_id", user.id);
    },
    onSuccess: () => {
      // Auto-save : pas de toast de succes (sinon spam a chaque changement).
      // L'UI affiche un indicateur discret "enregistre". Toast conserve sur erreur.
      queryClient.invalidateQueries({ queryKey: ["scraping-zones", teamId] });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Impossible de sauvegarder les zones.",
        variant: "destructive",
      });
    },
  });
}
